import { useCallback, useState, type DragEvent } from "react";

const MIME = "application/x-pipe-card";

export interface ColumnDrag {
  /** Id-en til kortet som dras akkurat nå. */
  draggingId: string | null;
  /** Kolonnen musepekeren er over (til drop-markering). */
  overColumn: string | null;
  cardProps: (id: string, enabled: boolean) => {
    draggable: boolean;
    onDragStart: (e: DragEvent) => void;
    onDragEnd: () => void;
  };
  columnProps: (column: string) => {
    onDragOver: (e: DragEvent) => void;
    onDragLeave: () => void;
    onDrop: (e: DragEvent) => void;
  };
}

/**
 * HTML5 dra-og-slipp mellom kolonner, uten bibliotek. `onDrop` får kortets
 * id og kolonnen det slippes i; hvilke flyttinger som er lov avgjøres av
 * kalleren.
 */
export function useColumnDrag(onDrop: (cardId: string, column: string) => void): ColumnDrag {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overColumn, setOverColumn] = useState<string | null>(null);

  const cardProps: ColumnDrag["cardProps"] = useCallback(
    (id, enabled) => ({
      draggable: enabled,
      onDragStart: (e) => {
        if (!enabled) return;
        e.dataTransfer.setData(MIME, id);
        e.dataTransfer.effectAllowed = "move";
        setDraggingId(id);
      },
      onDragEnd: () => {
        setDraggingId(null);
        setOverColumn(null);
      },
    }),
    [],
  );

  const columnProps: ColumnDrag["columnProps"] = useCallback(
    (column) => ({
      onDragOver: (e) => {
        if (!e.dataTransfer.types.includes(MIME)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setOverColumn(column);
      },
      onDragLeave: () => setOverColumn((c) => (c === column ? null : c)),
      onDrop: (e) => {
        e.preventDefault();
        const id = e.dataTransfer.getData(MIME);
        setDraggingId(null);
        setOverColumn(null);
        if (id) onDrop(id, column);
      },
    }),
    [onDrop],
  );

  return { draggingId, overColumn, cardProps, columnProps };
}
